import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom';
import GoogleTagManager from 'react-gtm-module';

import SettingsPage from '../SettingsPage/SettingsPage';
import { ChakraProvider, UseToastOptions } from '@chakra-ui/react';
import { PublicProfileStateProvider } from '../../context/public-profile-context';
import RefreshSessionPage from '../RefreshSessionPage/RefreshSessionPage';
import Login from '../Login/Login';
import Logout from '../Logout/Logout';
import { getCookie, removeCookie, setCookie } from '../../SharedLib/Util/CookieUtil';
import { setDefaultFetchConfig } from '../../SharedLib/Util/FetchUtil';
import { processUserSession } from '../../SharedLib/Util/CognitoUtil';
import { UserStateProvider } from '../../context/user-context';
import { changeStatusCodeInUserSessionCookies, parseAccessToken } from '../../SharedLib/Util/UserSessionUtil';
import PlanSelectionPage from '../PlanSelection/PlanSelectionPage';
import { PlanSelectionStateProvider } from '../../context/plan-selection-context';
import VerifyEmail from '../VerifyEmail/VerifyEmail';
import ClassroomInvitation from '../ClassroomInvitation/ClassroomInvitation';
import ProfileProvider from '../../context/profileProvider';
import chakraTheme from '../../themes/chakraui';
import { UnsavedChangesProvider } from '../../context/UnsavedChangesProvider';
import SkillsProvider from '../../context/SkillsProvider';
import SkillsImmutableProvider from '../../context/SkillsImmutableProvider';
/** Hosted UI OAuth callback — path `/social-login`, component {@link SocialLogin}. */
import SocialLogin from '../SocialLogin/SocialLogin';
/** Custom IdP consume callback — path `/social-auth`, component {@link ../CustomSocialAuth/CustomSocialAuth}. */
import CustomSocialAuth from '../CustomSocialAuth/CustomSocialAuth';
import { getEnv } from '../../SharedLib/Util/EnvironmentUtil';
import { logging } from '../../SharedLib/Util/LoggingUtil';

const defaultToastOptions: UseToastOptions = {
  position: 'top',
  isClosable: true,
  duration: 3500,
  status: 'info',
  containerStyle: {
    width: '800px',
    maxWidth: '95%',
  },
};

interface IBase {
  version: string;
}

function Base({ version }: IBase) {
  if (window.location.href.startsWith('http:')) {
    window.location.href = window.location.href.replace(/^http:/, 'https:');
  }

  const loadSignupOnce = parseInt(getCookie('load_signup_once') || '0');
  // console.log('on load -> loadSignupOnce: ', loadSignupOnce)

  const [dataLayerInitialized, setDataLayerInitialized] = useState(false);
  const gtmInitialized = useRef(false);

  // Initialize dataLayer early
  useEffect(() => {
    if (dataLayerInitialized) {
      return;
    }

    (window as any).dataLayer = (window as any).dataLayer || [];

    (window as any).dataLayer.push({
      js: new Date().toISOString(),
    });

    if (getEnv() !== 'develop') {
      logging.logDebug('Base -> useEffect -> dataLayer -> Not pushing to dataLayer in this environment');

      setDataLayerInitialized(true);
      return;
    }

    const accessToken = getCookie('accessToken');

    if (accessToken) {
      const accessTokenMeta = parseAccessToken({
        accessToken,
      });

      if (
        accessTokenMeta.error.code === '0' &&
        accessTokenMeta.data.payload &&
        typeof accessTokenMeta.data.payload.jti === 'string' &&
        accessTokenMeta.data.payload.jti
      ) {
        (window as any).dataLayer.push({
          user_id: accessTokenMeta.data.payload.jti,
        });
      }
    }

    setDataLayerInitialized(true);
  }, [dataLayerInitialized]);

  // Initialize GTM after dataLayer is ready
  useEffect(() => {
    if (gtmInitialized.current || !dataLayerInitialized) {
      return;
    }

    gtmInitialized.current = true;

    GoogleTagManager.initialize({
      gtmId: getEnv() === 'develop' ? 'GTM-NRVTS9TF' : 'GTM-KTCFC3S',
    });
  }, [dataLayerInitialized]);

  useEffect(() => {
    setDefaultFetchConfig({
      refreshTokenOnFailedCognitoAuth: true,
      processUserSessionRef: processUserSession,
      changeStatusCodeInUserSessionCookiesRef: changeStatusCodeInUserSessionCookies,
    });

    if (loadSignupOnce) {
      setCookie('load_signup_once', '0');
      removeCookie('load_signup_once');
    }
  }, []); // passing an empty array as second argument triggers the callback in useEffect only after the initial render thus replicating `componentDidMount` lifecycle behaviour

  return (
    <Router>
      <Switch>
        <Route path={['/classroom-invitation']}>
          <ClassroomInvitation />
        </Route>

        <Route path={['/signup', '/sign-up']}>
          <Login />
        </Route>

        <Route path={['/logout', '/log-out']}>
          <Logout />
        </Route>

        <Route path={['/verify-email', '/verify']}>
          <VerifyEmail />
        </Route>

        {/* Hosted UI: `code` + `state` → token exchange (`SocialLogin`). */}
        <Route path={['/social-login']}>
          <SocialLogin />
        </Route>

        {/* Custom IdP: `consume_token` → POST `/api/user/session` (`CustomSocialAuth`, still path `/social-auth`). */}
        <Route path={['/social-auth']}>
          <CustomSocialAuth />
        </Route>

        <Route path={['/settings', '/editprofile', '/edit-profile', '/profile']}>
          <ChakraProvider theme={chakraTheme} toastOptions={{ defaultOptions: defaultToastOptions }}>
            <UnsavedChangesProvider>
              <UserStateProvider>
                <PublicProfileStateProvider>
                  <ProfileProvider>
                    <SkillsImmutableProvider>
                      <SkillsProvider>
                        <SettingsPage version={version} />
                      </SkillsProvider>
                    </SkillsImmutableProvider>
                  </ProfileProvider>
                </PublicProfileStateProvider>
              </UserStateProvider>
            </UnsavedChangesProvider>
          </ChakraProvider>
        </Route>

        <Route path={['/refresh-session']}>
          <RefreshSessionPage />
        </Route>

        <Route path={['/plan-selection']}>
          <PlanSelectionStateProvider>
            <PlanSelectionPage />
          </PlanSelectionStateProvider>
        </Route>

        <Route path={['/', '/login', '/log-in']}>{loadSignupOnce !== 1 && <Login />}</Route>
      </Switch>
    </Router>
  );
}
export default Base;
